import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';

const DEBOUNCE_MS = 200;

interface NameValue {
  name: string;
  value: string;
}

interface AttendanceCorrectionComponentArgs {
  enabled: string;
  approvers: { userId: number; employeeId: number; name: string }[];
}

export default class AttendanceCorrectionComponent extends Component<AttendanceCorrectionComponentArgs> {
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare ajaxFetch: AjaxFetchService;

  @tracked approvers: NameValue[] = [];
  @tracked enabled: boolean = false;

  constructor(owner: unknown, args: AttendanceCorrectionComponentArgs) {
    super(owner, args);

    this.enabled = this.args.enabled === 'true';
    this.approvers = this.args.approvers.map((admin) => ({ name: admin.name, value: String(admin.userId) }));
  }

  get isDisabled(): boolean {
    return !this.approvers.length;
  }

  @action
  onApproversChange(approvers: NameValue[]): void {
    this.approvers = approvers;
    void this.saveTask.perform();
  }

  toggleTask = dropTask(async () => {
    if (this.isDisabled) {
      return;
    }

    this.enabled = !this.enabled;

    await this.saveTask.perform();

    this.metrics.trackEvent(`Toggle Attendance Correction`, {
      enabled: this.enabled,
    });
  });

  saveTask = dropTask(async () => {
    const url = urlBuilder.rto.settingsUrl(this.state.currentLocation.id);

    if (!this.approvers.length) {
      this.enabled = false;
    }

    try {
      await this.ajaxFetch.request(url, {
        type: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          attendanceCorrectionEnabled: this.enabled,
          attendanceCorrectionApprovers: this.approvers.map((a) => Number(a.value)),
        },
      });
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  searchTask = restartableTask(async (term: string) => {
    try {
      await timeout(zft(DEBOUNCE_MS));

      const approvers = await this.store.query('user-roles-digest', {
        filter: { name: term, roles: 'Location Admin,Global Admin', location: this.state.currentLocation.id },
      });

      return approvers.toArray().map((admin) => ({ name: admin.fullName, value: admin.id }));
    } catch (e: unknown) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return [];
    }
  });
}
